import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { tabStorage } from '../../../utils/tabStorage';

/**
 * Hard-blocks the browser's back / forward controls for the whole app.
 *
 * Mount once, inside <Router>.
 *
 * The rule, in the product owner's words: when the user opens the app it is
 * fine that the Back button is there and works. Once the user clicks ANY
 * button, from then on browser Back / Forward must not do anything — and
 * never by opening a new screen, tab or window. It applies to every route
 * (landing page, /login, /forgotpassword, every student / teacher page, the
 * guest assessment and preview flows); there is no logged-in / guest
 * distinction any more. Logout and Switch Role change nothing: the block
 * stays armed, and Back on /login simply stays on /login.
 *
 * Why "once the user clicks": Chromium's history-manipulation intervention
 * marks every same-document entry a page adds WITHOUT user activation as
 * skippable, and the toolbar Back button skips skippable entries. So entries
 * pushed at load time, or re-pushed from a popstate handler after a Back
 * press (a browser Back revokes the activation the intervention honours),
 * were simply skipped and the next press left the app — that was the "exits
 * the entire app" bug. Entries pushed synchronously inside a user gesture are
 * never marked skippable, and traversing history (history.go) never changes
 * skippability. So everything below is built inside the first gesture, and
 * afterwards this file only ever traverses — it never pushes again.
 *
 * How it works:
 *   1. Single-entry history (installSingleEntryHistory): window.history
 *      .pushState is rewritten to replaceState for the whole app, so in-app
 *      navigation (navigate('/x'), <Link>, <Navigate>) never adds entries and
 *      the live page is always the TOP entry of the tab. A fresh tab
 *      (history.length === 1) therefore keeps Back greyed out for the whole
 *      session. The originals are kept as history.__rawPushState /
 *      __rawReplaceState for this file's own use.
 *   2. Arming (arm, isArmingGesture): capture-phase window listeners for
 *      pointerup / click / touchend / keydown (not Escape, not a bare
 *      modifier, not a history shortcut itself — those grant no activation in
 *      any browser) arm the block at the first gesture. Where
 *      navigator.userActivation exists we only arm while isActive is true (a
 *      synthetic click grants nothing); where it is missing (older Safari /
 *      Firefox, jsdom) the gesture alone is enough. Before the first gesture
 *      this file does nothing at all: Back works, as the owner accepted.
 *   3. Trap stack (buildTrapStack), built synchronously INSIDE that first
 *      gesture when history.length > 1: the current entry is re-stamped with
 *      { ...state, __vtTrap: 0 } and TRAP_DEPTH (49) clones of it are
 *      raw-pushed with __vtTrap 1..49 — same URL, same react-router
 *      usr/key/idx, so react-router could never be confused by one. The live
 *      page now sits on top at position 49 (trapTop) over 49 inert traps.
 *      base + 49 = 50 = Chromium's and Firefox's session-history cap, which
 *      prunes the oldest entries: the page the user came from is evicted from
 *      the tab, and on Firefox (whose Back walks past never-interacted
 *      entries all the way down to index 0) index 0 is now our own base trap.
 *      Keep TRAP_DEPTH at 49 for that reason. Built once per document and
 *      never topped up — traversal never consumes entries, and a push made
 *      after a Back press is exactly what the intervention punishes. In a
 *      fresh tab there is nothing to build (Back is already grey), but the
 *      gesture listeners keep watching in case the tab ever grows.
 *   4. popstate interceptor (onPopStateCapture), registered at module import
 *      — before <Router> mounts — with capture: true, so it runs before
 *      react-router's own non-capture listener (capture-before-bubble at the
 *      target holds in every current engine). Landing on a trap (marker <
 *      trapTop) is never legitimate: stopImmediatePropagation() so
 *      react-router never hears of it (no re-render, no flash), then
 *      history.go(trapTop - pos) straight back to the top. Arriving back at
 *      the top (marker === trapTop) is our own return and is swallowed the
 *      same way. Anything else (an entry without a marker, e.g. a stray
 *      fragment entry) passes through to react-router and the React-level
 *      fallback in step 8. Nothing is ever pushed from this path; rapid
 *      presses that land deeper simply jump back from wherever they land, so
 *      the stack never degrades no matter how many presses come. A watchdog
 *      re-issues the return if the browser dropped the go() (Chromium and
 *      Firefox throttle at 200 same-document navigations per 10 s).
 *   5. Marker-preserving replaceState: react-router's replace (and our
 *      rewritten push) would overwrite the top's marker with a fresh
 *      { usr, key, idx }. The replaceState wrapper copies the current entry's
 *      marker onto the new state, so the live entry always reads
 *      __vtTrap === trapTop, and a trap keeps its position even if a
 *      timer-driven navigate() ever lands on one while a return is in
 *      flight. The wrapper also records the live entry (href + state) and
 *      persists the live URL to sessionStorage on every in-app navigation.
 *      If a return ever arrives at a top whose URL differs from the live one,
 *      the top is replaceState'd back to it — replace never marks anything
 *      skippable, and it is the only write this file ever makes after a
 *      Back press.
 *   6. Reload self-heal (healTrapLandingOnBoot): a reload or any full
 *      navigation starts a new document, un-armed until the next click, with
 *      the old document's traps below it as cross-document entries. At module
 *      import, before react-router boots, a marker in history.state is
 *      compared with the trapTop the build persisted to sessionStorage:
 *        - marker === persisted trapTop: the LIVE page itself was reloaded
 *          (F5, ChunkErrorBoundary's window.location.reload(), the reload
 *          buttons in TestAttempt / TestReviewpage, Chrome's "Duplicate
 *          tab"). Browsers keep history.state across a reload, and because
 *          of step 5 the live top always carries the marker, so this is the
 *          normal case — NOT a landing. Only the marker is dropped: URL and
 *          react-router's { usr, key, idx } stay, so every screen that reads
 *          location.state (AssessmentPage's activeTab, LOOQSearch's results,
 *          ...) still has it after the reload, exactly as before this file
 *          existed.
 *        - anything else (below the top; above it when an older document
 *          built deeper; or no persisted top at all because logout wiped
 *          sessionStorage): a cross-document landing on a trap, i.e. a full
 *          load of a stale URL whose usr is a build-time clone. The entry is
 *          replaced with the persisted live URL and a null state.
 *      trapTop itself (the module variable) is never carried across
 *      documents — positions below a new document are meaningless to it; the
 *      persisted copy is read only by this comparison.
 *   7. Also armed at the first gesture: keyboard (Alt/Cmd+Arrow, Cmd+[ / ],
 *      Backspace) and mouse (side buttons) history shortcuts are cancelled,
 *      and horizontal overscroll (trackpad swipe) is disabled via the
 *      `app-nav-locked` class on <html> (rule in src/index.css).
 *   8. React-level fallback (the NavigationGuard component): a POP that did
 *      reach react-router while armed and changed the path is reversed with
 *      navigate(prev, { replace: true }) in a useLayoutEffect (before paint).
 *      correctionInFlightRef is set synchronously there so PublicRoute (which
 *      wraps / and /login and redirects an authenticated visitor from a
 *      passive effect — i.e. after this layout effect for the same commit)
 *      skips its own redirect for that one transient commit instead of
 *      overwriting the correction. Confirmed with a real-Chromium Playwright
 *      repro back when every POP went this way; with the trap stack in place
 *      it is a last resort that should never be needed.
 *
 * Accepted limitations, all by design of the intervention: before the first
 * click Back works normally; a reload or any full navigation starts a new
 * document that is un-armed until the next click; Android closes the tab if
 * Back has nothing non-skippable to go to. Traps keep the URL and title the
 * page had when the stack was built (typically /login or the landing page), so
 * Chrome's long-press Back menu lists 49 identical copies and the address bar
 * shows that URL for the duration of each absorbed press. Safari does not
 * honour overscroll-behavior for its swipe gesture, so there the swipe is
 * absorbed after the fact by step 4. Safari throws SecurityError after 100
 * pushState + replaceState calls per 10 s per document, which is why the
 * build is 50 calls, once, each in try/catch.
 *
 * Never call window.open / window.close here, for any reason. The old
 * "relaunch in a fresh window on first click" behaviour is gone and must not
 * come back in any form.
 *
 * In-app navigation is untouched. Do not use navigate(-1) / history.back()
 * anywhere: it is a round trip the interceptor swallows. Navigate to an
 * explicit route instead.
 *
 * jsdom (tests): window.history.pushState is the rewritten version once this
 * module is imported, so tests simulating pre-app history must use
 * history.__rawPushState; traversal runs on queued tasks, so tests settle
 * between presses; __resetForTests() clears the per-document state.
 */
export const correctionInFlightRef = { current: false };

export const LOCK_CLASS = 'app-nav-locked';

// history.state marker carried by every entry of the trap stack: 0..trapTop-1
// below the live page, trapTop on the live page itself.
export const TRAP_KEY = '__vtTrap';

// base + TRAP_DEPTH = 50 = Chromium's / Firefox's session-history cap (step 3).
export const TRAP_DEPTH = 49;

// sessionStorage (tabStorage) key holding the live URL (path+search+hash), step 6.
export const LIVE_URL_STORAGE_KEY = 'navGuardLiveUrl';

// sessionStorage (tabStorage) key holding trapTop as built in this document,
// so that the NEXT document (after a reload) can tell a reloaded live top from
// a trap landing (step 6). Written by buildTrapStack, read only at boot.
export const TRAP_TOP_STORAGE_KEY = 'navGuardTrapTop';

// Delays for re-issuing a return the browser dropped (rate limit), step 4.
const RETURN_RETRY_DELAYS_MS = [250, 1000, 4000, 10000];

const GESTURE_EVENTS = ['pointerup', 'click', 'touchend', 'keydown'];

// Keys that are never a gesture on their own.
const BARE_MODIFIER_KEYS = [
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'AltGraph',
  'CapsLock',
  'NumLock',
  'ScrollLock',
  'Fn',
  'FnLock',
  'Hyper',
  'Super',
  'Symbol',
  'SymbolLock',
  'OS',
];

// Per-document state. Deliberately never read back from storage: a previous
// document's positions mean nothing to this one (step 6). The one exception is
// the persisted COPY of trapTop, which healTrapLandingOnBoot compares against
// the marker it booted with — nothing else ever reads it.
let armed = false;
let trapTop = null; // highest marker in this document; null until the stack is built
let live = null; // { href, state } of the live (top) entry
let returnWatchdog = null;

const readTrapPosition = (state) =>
  state && typeof state === 'object' && typeof state[TRAP_KEY] === 'number'
    ? state[TRAP_KEY]
    : null;

const withTrapPosition = (state, pos) => ({
  ...(state && typeof state === 'object' ? state : {}),
  [TRAP_KEY]: pos,
});

const withoutTrapPosition = (state) => {
  if (!state || typeof state !== 'object' || !(TRAP_KEY in state)) return state ?? null;
  const { [TRAP_KEY]: ignored, ...rest } = state;
  return Object.keys(rest).length ? rest : null;
};

const currentLiveUrl = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const persistLiveUrl = () => {
  tabStorage.setItem(LIVE_URL_STORAGE_KEY, currentLiveUrl());
};

const persistTrapTop = (top) => {
  tabStorage.setItem(TRAP_TOP_STORAGE_KEY, String(top));
};

// The trapTop the previous document (same tab) built, or null when nothing
// usable is stored — logout clears sessionStorage, and garbage must never
// pass for a match.
const readPersistedTrapTop = () => {
  const raw = tabStorage.getItem(TRAP_TOP_STORAGE_KEY);
  if (raw === null || raw === '') return null;
  const top = Number(raw);
  return Number.isInteger(top) && top >= 0 ? top : null;
};

// Hook run by the history wrappers after every in-app navigation (step 5).
const rememberLive = () => {
  live = { href: window.location.href, state: window.history.state };
  persistLiveUrl();
};

/**
 * Single-history-entry mode (step 1) plus the marker-preserving replace
 * (step 5). Browsers grey out Back/Forward only when the tab has no entries to
 * go to, and JS can't set that directly — the only way to get there is to
 * never add entries. So every pushState (react-router's PUSH, i.e.
 * navigate('/x') and <Link>) is turned into replaceState. Both wrappers copy
 * the current entry's trap marker onto the new state and then report the new
 * live entry through history.__vtOnNavigate (re-pointed on every evaluation
 * of this module so a hot reload doesn't leave the wrappers calling into a
 * stale closure).
 */
const installSingleEntryHistory = () => {
  if (typeof window === 'undefined' || !window.history) return;
  const h = window.history;
  h.__vtOnNavigate = rememberLive;
  if (h.__singleEntryInstalled) return;
  const rawPush = h.pushState.bind(h);
  const rawReplace = h.replaceState.bind(h);
  h.__rawPushState = rawPush;
  h.__rawReplaceState = rawReplace;
  const replaceKeepingTrap = (state, title, url) => {
    const pos = readTrapPosition(h.state);
    rawReplace(pos === null ? state : withTrapPosition(state, pos), title, url);
    if (typeof h.__vtOnNavigate === 'function') h.__vtOnNavigate();
  };
  h.pushState = replaceKeepingTrap;
  h.replaceState = replaceKeepingTrap;
  h.__singleEntryInstalled = true;
};

/**
 * Reload self-heal (step 6). Called at module import, before react-router
 * boots, whenever this document's entry carries a marker from a previous
 * document:
 *   - marker === the trapTop that document persisted: the live page was
 *     reloaded. Keep URL and react-router state, drop only the marker.
 *   - otherwise: loaded ON a trap entry (a cross-document Back into a previous
 *     document's stack). Swap the stale URL for the persisted live URL and
 *     drop the state, whose usr is a build-time clone.
 * Returns true when it did something.
 */
export const healTrapLandingOnBoot = () => {
  if (typeof window === 'undefined' || !window.history || !window.history.__rawReplaceState) {
    return false;
  }
  const h = window.history;
  const pos = readTrapPosition(h.state);
  if (pos === null) return false;
  const previousTop = readPersistedTrapTop();
  try {
    if (previousTop !== null && pos === previousTop) {
      h.__rawReplaceState(withoutTrapPosition(h.state), '', window.location.href);
    } else {
      const liveUrl = tabStorage.getItem(LIVE_URL_STORAGE_KEY);
      h.__rawReplaceState(null, '', liveUrl || currentLiveUrl());
    }
  } catch (e) {
    return false;
  }
  return true;
};

/**
 * Trap stack (step 3). Must run synchronously inside a user gesture. Builds
 * once per document; a tab with nothing below the app (history.length === 1)
 * is left alone and re-checked on the next gesture. Every history call is
 * try/catch'd: a Safari rate-limit SecurityError part-way through just leaves
 * a shallower stack, with trapTop set to what actually got pushed.
 */
const buildTrapStack = () => {
  const h = window.history;
  if (trapTop !== null || !h || !h.__rawPushState || !h.__rawReplaceState || h.length <= 1) {
    return false;
  }
  const base = h.state;
  const href = window.location.href;
  let pushed = 0;
  try {
    h.__rawReplaceState(withTrapPosition(base, 0), '', href);
    for (let i = 1; i <= TRAP_DEPTH; i += 1) {
      h.__rawPushState(withTrapPosition(base, i), '', href);
      pushed = i;
    }
  } catch (e) {
    /* rate-limited or history unavailable: keep whatever got built */
  }
  trapTop = pushed;
  live = { href, state: h.state };
  persistLiveUrl();
  // For the next document only (step 6): after a reload the live top still
  // reads __vtTrap === this value, and that is how it is told from a landing.
  persistTrapTop(pushed);
  return true;
};

const cancelReturnWatchdog = () => {
  if (returnWatchdog !== null) {
    window.clearTimeout(returnWatchdog);
    returnWatchdog = null;
  }
};

const goToTop = (pos) => {
  try {
    window.history.go(trapTop - pos);
  } catch (e) {
    /* traversal refused: the watchdog retries */
  }
};

// If the browser silently dropped our go() (rate limit, see step 4), we are
// still parked on a trap: re-issue it with backoff until we are home.
const scheduleReturnWatchdog = (attempt) => {
  cancelReturnWatchdog();
  if (attempt >= RETURN_RETRY_DELAYS_MS.length) return;
  returnWatchdog = window.setTimeout(() => {
    returnWatchdog = null;
    if (trapTop === null) return;
    const pos = readTrapPosition(window.history.state);
    if (pos === null || pos >= trapTop) return;
    goToTop(pos);
    scheduleReturnWatchdog(attempt + 1);
  }, RETURN_RETRY_DELAYS_MS[attempt]);
};

// popstate interceptor (step 4). Capture phase, registered at module import.
const onPopStateCapture = (event) => {
  if (trapTop === null) return;
  const h = window.history;
  const pos = readTrapPosition(h.state);
  // Not one of ours: react-router and the React-level fallback handle it.
  if (pos === null || pos > trapTop) return;

  event.stopImmediatePropagation();

  if (pos < trapTop) {
    goToTop(pos);
    scheduleReturnWatchdog(0);
    return;
  }

  // Back at the top: our own return arriving. "Marker === trapTop" is the
  // whole test (there is no pendingReturns counter): it is exact as long as
  // the live top is the only marker-trapTop entry in the document, which the
  // single-entry history (step 1) guarantees. The one thing that would break
  // it is a fragment navigation (href="#x", location.hash = ...): that adds
  // an UNMARKED entry above the top, and a Back from it onto the top would be
  // swallowed here without react-router hearing it, leaving react-router's
  // location (with the hash) out of sync with the address bar until the next
  // in-app navigation. Nothing in src does fragment navigation today; if that
  // changes, count pending returns instead of trusting the marker.
  cancelReturnWatchdog();
  if (live && window.location.href !== live.href) {
    // A navigate() ran while the return was in flight and landed on a trap,
    // so the top is stale relative to what React is showing. replaceState
    // (never marked skippable) brings it back in line.
    try {
      const state = withTrapPosition(live.state, trapTop);
      h.__rawReplaceState(state, '', live.href);
      live = { href: live.href, state };
    } catch (e) {
      /* leave it: the next in-app navigation rewrites the top anyway */
    }
  }
};

const installPopStateInterceptor = () => {
  if (typeof window === 'undefined') return;
  // Replace rather than add, so a hot reload never leaves two interceptors
  // (with two sets of per-document state) racing each other.
  if (typeof window.__vtOnPopState === 'function') {
    window.removeEventListener('popstate', window.__vtOnPopState, true);
  }
  window.__vtOnPopState = onPopStateCapture;
  window.addEventListener('popstate', onPopStateCapture, true);
};

installSingleEntryHistory();
healTrapLandingOnBoot();
installPopStateInterceptor();

const isEditableTarget = (target) => {
  if (!target || typeof target.tagName !== 'string') return false;
  const tag = target.tagName.toUpperCase();
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable === true
  );
};

// Keyboard shortcuts that browsers map to history back / forward.
export const isHistoryShortcut = (event) => {
  const key = event.key;
  const editable = isEditableTarget(event.target);
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    // Alt+Arrow (Windows/Linux/Chrome), Cmd+Arrow (Safari). Inside a text
    // field these move the caret, so leave them alone there.
    return (event.altKey || event.metaKey) && !editable;
  }
  if (key === '[' || key === ']') {
    // Cmd+[ / Cmd+] (macOS browsers)
    return event.metaKey === true;
  }
  if (key === 'Backspace') {
    // Legacy back shortcut outside editable fields.
    return !editable && !event.ctrlKey && !event.metaKey && !event.altKey;
  }
  return false;
};

const isHistoryMouseButton = (event) => event.button === 3 || event.button === 4;

/**
 * Arming gesture (step 2). Any pointerup / click / touchend, or a keydown that
 * is not Escape, a bare modifier, or a history shortcut (none of which grant
 * activation — and cancelling the very press that "opened" the app is not
 * what the owner asked for). With navigator.userActivation available, only
 * while isActive is true; without it, the gesture alone counts.
 */
export const isArmingGesture = (event) => {
  if (event.type === 'keydown') {
    const key = event.key;
    if (key === 'Escape' || key === 'Esc' || BARE_MODIFIER_KEYS.includes(key)) return false;
    if (isHistoryShortcut(event)) return false;
  }
  const activation =
    typeof window !== 'undefined' && window.navigator ? window.navigator.userActivation : undefined;
  if (activation && typeof activation.isActive === 'boolean') return activation.isActive;
  return true;
};

const arm = () => {
  if (!armed) {
    armed = true;
    document.documentElement.classList.add(LOCK_CLASS);
  }
  if (trapTop === null) buildTrapStack();
};

// Tests only: forget this document's state and strip the live marker, so each
// test starts un-armed with no stack.
export const __resetForTests = () => {
  cancelReturnWatchdog();
  armed = false;
  trapTop = null;
  live = null;
  correctionInFlightRef.current = false;
  const h = window.history;
  if (h && h.__rawReplaceState && readTrapPosition(h.state) !== null) {
    h.__rawReplaceState(withoutTrapPosition(h.state), '', window.location.href);
  }
  document.documentElement.classList.remove(LOCK_CLASS);
  tabStorage.removeItem(LIVE_URL_STORAGE_KEY);
  tabStorage.removeItem(TRAP_TOP_STORAGE_KEY);
};

const locationToPath = (location) =>
  `${location.pathname}${location.search}${location.hash}`;

const NavigationGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const prevRef = useRef(null); // { path, state } for the last render's location

  // React-level fallback (step 8). Runs before paint, so a POP that got
  // through is reversed before the user ever sees the page it landed on.
  useLayoutEffect(() => {
    const path = locationToPath(location);
    const prev = prevRef.current;

    // A browser back/forward press reached react-router and changed the URL
    // while the block is armed. Before the first gesture POPs are left alone.
    const blockedPop =
      armed && navigationType === 'POP' && prev !== null && prev.path !== path;

    if (blockedPop) {
      // Set before navigate(): PublicRoute's passive-effect redirect (for a
      // transient /login or / landing) checks this and must see it already
      // true, per the layout-before-passive ordering guarantee.
      correctionInFlightRef.current = true;
      navigate(prev.path, { replace: true, state: prev.state ?? null });
      // Don't update prevRef here: the replace above re-fires this effect
      // with the corrected location, which will record it then.
      return;
    }

    correctionInFlightRef.current = false;
    prevRef.current = { path, state: location.state };
    persistLiveUrl();
  }, [location, navigationType, navigate]);

  // Window listeners are registered once; they consult the module state.
  useLayoutEffect(() => {
    if (armed) document.documentElement.classList.add(LOCK_CLASS);

    const onGesture = (event) => {
      if (isArmingGesture(event)) arm();
    };
    const onKeyDown = (event) => {
      if (armed && isHistoryShortcut(event)) event.preventDefault();
    };
    const onMouse = (event) => {
      if (armed && isHistoryMouseButton(event)) event.preventDefault();
    };

    // Gesture listeners first, so the shortcut/mouse handlers for the same
    // event already see the block armed.
    GESTURE_EVENTS.forEach((type) => window.addEventListener(type, onGesture, true));
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('mousedown', onMouse, true);
    window.addEventListener('mouseup', onMouse, true);
    window.addEventListener('auxclick', onMouse, true);

    return () => {
      GESTURE_EVENTS.forEach((type) => window.removeEventListener(type, onGesture, true));
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('mousedown', onMouse, true);
      window.removeEventListener('mouseup', onMouse, true);
      window.removeEventListener('auxclick', onMouse, true);
      document.documentElement.classList.remove(LOCK_CLASS);
    };
  }, []);

  return null;
};

export default NavigationGuard;
